import { PlatformIcon } from '@/components/creator/PlatformIcon';
import { Tooltip } from '@/components/ui/tooltip';
import { Button, type ButtonProps } from '@chakra-ui/react';
import type { MentionPlatform } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

export interface ViewOnPlatformIconButtonProps extends Omit<ButtonProps, 'aria-label'> {
  platform: MentionPlatform;
  /** The publication's own address, when the mention carries one. */
  permalink?: string | null;
  /** The creator's profile, the fallback address for a story. */
  profileUrl?: string | null;
  /**
   * Spell the destination out beside the platform mark, as a pill made to sit
   * over media (the preview's top bar). Without it, a bare icon with a tooltip.
   */
  withLabel?: boolean;
}

const ICON_SIZE = 16;

/**
 * Opens the mention where it was published, on its own platform.
 *
 * A story has no permalink and never will: the creator's profile is the nearest
 * true address, so the button falls back to it and says so, rather than
 * promising a publication it cannot open. Knowing neither address, it renders
 * nothing instead of a control that leads nowhere.
 */
export function ViewOnPlatformIconButton({
  platform,
  permalink,
  profileUrl,
  withLabel = false,
  ...buttonProps
}: ViewOnPlatformIconButtonProps) {
  const { t } = useTranslation();
  const href = permalink || profileUrl || null;

  if (!href) {
    return null;
  }

  const label = t(`mentions.preview.${permalink ? 'viewOn' : 'viewProfileOn'}.${platform}`);
  const link = (
    <a href={href} target="_blank" rel="noopener noreferrer">
      <PlatformIcon platform={platform} size={withLabel ? 14 : ICON_SIZE} decorative />
      {withLabel && label}
    </a>
  );

  if (withLabel) {
    return (
      <Button
        asChild
        variant="ghost"
        size="xs"
        h={6}
        px={2.5}
        gap={1.5}
        rounded="full"
        bg="whiteAlpha.900"
        color="black"
        fontSize="xs"
        fontWeight="medium"
        _hover={{ bg: 'white' }}
        aria-label={label}
        {...buttonProps}
      >
        {link}
      </Button>
    );
  }

  return (
    <Tooltip content={label}>
      <Button
        asChild
        variant="ghost"
        colorPalette="gray"
        size="sm"
        minW={8}
        boxSize={8}
        p={0}
        aria-label={label}
        {...buttonProps}
      >
        {link}
      </Button>
    </Tooltip>
  );
}

export default ViewOnPlatformIconButton;
